import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Framework, getTrackFunc } from '@/framework';
import type { ImageLayer } from '@/layers/image-layer';
import { type ContextMenuPlugin } from '@/plugins/context-menu/context-menu';
import type { MouseOperate } from '@/plugins/mouse-operate';
import { adjustImageLayerRatio, isLayerSizeRadio, rotateImage } from '@/utils/image-layer-crop';
import type { IImageLayerProps, ILayerProps } from '@/version-types';
import { getCropImgState, watchCropImgState } from './states/crop-img-state';

/**
 * 清理图片裁剪期间使用的运行时标记
 *
 * @param layer - 待写回画布或历史记录的图层
 * @returns 不再处于裁剪和基础操作状态的新图层
 */
export const clearCropRuntimeFlags = (layer: ILayerProps): ILayerProps => ({
    ...layer,
    __isCropImage: false,
    __isBasicOperating: undefined,
});

/**
 * 将本地裁剪字段合并到最新正式图片图层
 *
 * 最新图层提供特效、透明度、蒙版、层级和其他业务字段；本地草稿仅覆盖裁剪几何与图片变换。
 *
 * @param latestLayer - 确认裁剪时读取的最新正式图层
 * @param cropDraft - 当前本地裁剪草稿
 * @returns 清理裁剪运行时标记后的合并图层
 */
export const mergeCropDraftIntoLatestLayer = (latestLayer: ILayerProps, cropDraft: ILayerProps): ILayerProps => {
    const mergedLayer = clearCropRuntimeFlags(cloneDeep(latestLayer));
    const latestData = mergedLayer.data as IImageLayerProps;
    const draftData = cropDraft.data as IImageLayerProps;

    mergedLayer.left = cropDraft.left;
    mergedLayer.top = cropDraft.top;
    mergedLayer.width = cropDraft.width;
    mergedLayer.height = cropDraft.height;
    mergedLayer.rotate = cropDraft.rotate;
    latestData.imageTransform = cloneDeep(draftData.imageTransform);
    return mergedLayer;
};

/** 图片裁剪会话控制器 */
export class CropImgPlugin extends Framework {
    private rootTarget: HTMLElement | null = null;
    private croppingLayerId: string | null = null;
    private sessionGeneration = 0;

    private get store() {
        return getCropImgState(this.framework.identity);
    }

    private getImageLayer() {
        return this.framework.service.registration.getInstance<ImageLayer>('ImageLayer');
    }

    private getContextMenu() {
        return this.framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin');
    }

    private getMouseOperate(): MouseOperate | null {
        return this.framework.service.registration.getInstance<MouseOperate>('MouseOperate') || null;
    }

    private refreshFrame(frameId: number | null) {
        if (typeof frameId !== 'number') return;
        this.framework.service.edgelessCanvas.addDirtyFrameId(frameId);
        this.framework.service.edgelessCanvas.requestRenderAll();
    }

    private finishSession(activeLayerId?: string) {
        this.sessionGeneration += 1;
        this.getMouseOperate()?.setActiveRenderEnabled(true);
        this.getContextMenu()?.enableMenu();
        this.framework.service.editor.setDisablePick(false);
        this.croppingLayerId = null;
        this.rootTarget = null;
        this.store.reset();
        if (activeLayerId) {
            this.framework.service.frame.setActiveLayer(activeLayerId);
        }
    }

    /**
     * 打开裁剪会话
     *
     * 从正式图层深拷贝出独立草稿，后续预览不会覆盖正式业务数据。
     *
     * @param layerId - 目标图片图层 ID；省略时使用当前激活图片
     * @returns 裁剪草稿；找不到目标图层时返回 null
     *
     * 副作用：设置裁剪运行时标记并激活裁剪视图。
     */
    open(layerId?: string) {
        if (this.croppingLayerId) {
            this.cancel();
        }

        const mouseOperate = this.getMouseOperate();
        if (!mouseOperate) return null;
        const latestLayer = layerId
            ? this.framework.service.flatLayer.getLayer(layerId, true)
            : this.framework.service.frame.getActivePickLayer(true);
        if (!latestLayer) return null;

        const draftLayer = {
            ...cloneDeep(latestLayer),
            __isCropImage: true,
            __isBasicOperating: true,
        };
        this.sessionGeneration += 1;
        mouseOperate.setActiveRenderEnabled(false);
        this.croppingLayerId = latestLayer.id;
        this.store.setCurrentLayer(draftLayer);
        this.store.setIsAutoCutoutLayer(!isLayerSizeRadio(draftLayer.width, draftLayer.height));
        this.store.setIsCropImage(true);

        // 正式图层只写入控制显隐的运行时标记，业务数据仍保持打开时的最新值。
        this.framework.service.frame.addOrUpdateLayer({
            ...latestLayer,
            __isCropImage: true,
            __isBasicOperating: true,
        });
        this.getContextMenu()?.disableMenu();
        // 裁剪期间禁用普通图层选择，同时保留当前图片供裁剪面板使用。
        this.framework.service.editor.setDisablePick(true);
        this.framework.service.frame.setActiveLayer(latestLayer.id);
        return cloneDeep(draftLayer);
    }

    /**
     * 获取当前裁剪草稿
     *
     * @returns 当前草稿的深拷贝；没有会话时返回 null
     */
    getCurrentLayer() {
        return this.store.currentLayer ? cloneDeep(this.store.currentLayer) : null;
    }

    /**
     * 监听裁剪草稿和应用状态变化
     *
     * @param callback - 状态变化回调
     * @returns 取消监听函数
     */
    watchCurrentLayer(callback: (layer: ILayerProps | null, isApplying: boolean) => void) {
        return watchCropImgState(this.framework.identity, (state) => {
            callback(state.currentLayer ? cloneDeep(state.currentLayer) : null, state.isApplying);
        });
    }

    /**
     * 更新当前裁剪草稿
     *
     * @param layer - 手势或参数计算得到的新草稿
     * @returns 是否成功更新当前会话
     */
    updateCurrentLayer(layer: ILayerProps) {
        if (!this.croppingLayerId || layer.id !== this.croppingLayerId) return false;
        this.store.setCurrentLayer(cloneDeep(layer));
        return true;
    }

    /**
     * 更新裁剪框比例
     *
     * @param ratio - 比例值；`0` 表示自由裁剪
     * @returns 更新后的裁剪草稿
     */
    setRatio(ratio: string) {
        const currentLayer = this.getCurrentLayer();
        if (!currentLayer) return null;
        if (ratio === '0') {
            this.store.setIsAutoCutoutLayer(true);
            return currentLayer;
        }

        this.store.setIsAutoCutoutLayer(false);
        const nextLayer = adjustImageLayerRatio(currentLayer, ratio);
        this.updateCurrentLayer(nextLayer);
        return cloneDeep(nextLayer);
    }

    /**
     * 更新图片在裁剪框内的旋转角度
     *
     * @param angle - 图片旋转角度
     * @returns 更新后的裁剪草稿
     */
    setRotate(angle: number) {
        const currentLayer = this.getCurrentLayer();
        if (!currentLayer) return null;
        const nextLayer = rotateImage(currentLayer, angle);
        this.updateCurrentLayer(nextLayer);
        return cloneDeep(nextLayer);
    }

    /**
     * 取消裁剪并回显正式最新图层
     *
     * @returns 是否取消了当前裁剪会话
     *
     * 不写历史、不发送协同，仅恢复显示并重绘所属页面。
     */
    cancel() {
        const layerId = this.croppingLayerId;
        if (!layerId) {
            this.finishSession();
            return false;
        }

        const frameId = this.framework.service.frame.getLayerFrameId(layerId);
        const latestLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (latestLayer) {
            this.framework.service.frame.addOrUpdateLayer(clearCropRuntimeFlags(latestLayer), frameId ?? undefined);
        }
        this.finishSession(latestLayer?.id);
        this.refreshFrame(typeof frameId === 'number' ? frameId : null);
        return true;
    }

    /**
     * 应用当前裁剪草稿
     *
     * @returns 是否成功把裁剪字段合并到最新正式图层并提交
     *
     * 副作用：写入正式图层和一条历史；协同由历史写入链路统一发送，并重绘所属页面。
     */
    async apply() {
        const layerId = this.croppingLayerId;
        const currentLayer = this.getCurrentLayer();
        if (!layerId || !currentLayer || this.store.isApplying) return false;

        const sessionGeneration = this.sessionGeneration;
        this.store.setIsApplying(true);
        try {
            const latestLayer = this.framework.service.flatLayer.getLayer(layerId, true);
            if (!latestLayer) return false;

            const frameId = this.framework.service.frame.getLayerFrameId(layerId);
            const oldLayer = clearCropRuntimeFlags(cloneDeep(latestLayer));
            const finalLayer = mergeCropDraftIntoLatestLayer(latestLayer, currentLayer);

            if (isEqual(oldLayer, finalLayer)) {
                this.framework.service.frame.addOrUpdateLayer(oldLayer, frameId ?? undefined);
                this.finishSession(oldLayer.id);
                this.refreshFrame(typeof frameId === 'number' ? frameId : null);
                return true;
            }

            const committedLayer = await this.getImageLayer().updateLayer(
                finalLayer,
                oldLayer,
                true,
                getTrackFunc(this.framework.identity, oldLayer, 'crop'),
                {
                    canCommit: () => sessionGeneration === this.sessionGeneration && layerId === this.croppingLayerId,
                },
            );
            this.finishSession(committedLayer.id);
            this.refreshFrame(typeof frameId === 'number' ? frameId : null);
            return true;
        } catch (error) {
            if (sessionGeneration === this.sessionGeneration) {
                console.error('Apply crop image failed', error);
            }
            return false;
        } finally {
            if (sessionGeneration === this.sessionGeneration) {
                this.store.setIsApplying(false);
            }
        }
    }

    /**
     * 关闭裁剪会话
     *
     * 未应用的草稿按取消处理，不会发送协同数据。
     *
     * @returns 是否取消了当前裁剪会话
     */
    close() {
        return this.cancel();
    }

    /** 销毁插件并取消未提交会话 */
    destroy() {
        this.cancel();
    }

    /**
     * 判断事件目标是否属于裁剪操作区域
     *
     * @param target - 事件目标
     * @returns 是否命中裁剪操作区域
     */
    checkIsOperateTarget(target: EventTarget | null) {
        return (this.rootTarget?.getElementsByClassName('base-operate')?.[0] as HTMLDivElement)?.contains(
            target as Node,
        );
    }

    /**
     * 设置裁剪操作根节点
     *
     * @param target - 裁剪操作根节点
     */
    setRootTarget(target: HTMLElement | null) {
        this.rootTarget = target;
    }
}
